import { FC, ReactNode } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import styles from './PunchupPictureTile.module.css';

export interface PunchupPictureTileProps {
	/** Primary entity name — 16px / 600. */
	name: string;
	/** Square image shown on the left. A neutral placeholder renders when absent. */
	imageUrl?: string;
	/** Alt text for the image. Defaults to `name`. */
	imageAlt?: string;
	/** Small muted label above the name. */
	title?: string;
	/** Muted lines below the name (e.g. a price). */
	descriptions?: string[];
	/**
	 * Top-right corner slot — a small glyph or badge (e.g. an order number), the picture-tile equivalent
	 * of `PunchupTile.topRight`. The caller composes the node; the tile only positions it.
	 */
	topRight?: ReactNode;
	/** When provided the whole tile becomes a button (orange hover border). Omit for a static card. */
	onClick?: () => void;
	dataCy?: string;
}

/**
 * PunchupPictureTile — entity card with a square image on the left.
 *
 * The image-leading sibling of `PunchupEntityTile` (which has no media slot). A standalone card (its own
 * `<button>` / `<div>`) so the image sits *inside* the bordered tile, mirroring `PunchupTicketTypeTile`.
 * Generic: any entity that reads as "thumbnail + name + a line or two" (add-ons, merch, line items).
 * The tile IS the button — never nest a button inside (root Punchup invariant 6).
 */
export const PunchupPictureTile: FC<PunchupPictureTileProps> = ({
	name,
	imageUrl,
	imageAlt,
	title,
	descriptions,
	topRight,
	onClick,
	dataCy,
}) => {
	const content = (
		<>
			<div className={styles.media}>
				{imageUrl ? (
					<img className={styles.image} src={imageUrl} alt={imageAlt ?? name} />
				) : (
					<span className={styles.placeholder} aria-hidden>
						<ImageIcon size={20} />
					</span>
				)}
			</div>
			<div className={styles.info}>
				{title && <span className={styles.title}>{title}</span>}
				<span className={styles.name}>{name}</span>
				{descriptions?.map((line, i) => (
					<span key={i} className={styles.meta}>
						{line}
					</span>
				))}
			</div>
			{topRight && <div className={styles.topRight}>{topRight}</div>}
		</>
	);

	if (onClick) {
		return (
			<button type="button" className={styles.tileButton} onClick={onClick} data-cy={dataCy}>
				{content}
			</button>
		);
	}

	return (
		<div className={styles.tile} data-cy={dataCy}>
			{content}
		</div>
	);
};
